import { Item, permissionCheck } from "layouts/Config";
import SideNavItem from "./SideNavItem";
import { Box, Stack } from "@mui/material";
import { useState, useCallback } from "react";

interface CustomProps {
  items?: Item[];
  pathname?: string | null;
  subheader?: string;
  isCollapsed: boolean;
}

const renderItems = ({
  depth = 0,
  items,
  pathname,
  isCollapsed,
  openItems,
  onToggle,
  onItemClick,
}: {
  depth?: number;
  items: any;
  pathname?: string | null;
  isCollapsed: boolean;
  openItems: Set<string>;
  onToggle: (title: string) => void;
  onItemClick: (title: string, hasChildren: boolean, depth: number) => void;
}): JSX.Element[] =>
  items.reduce(
    (acc: JSX.Element[], item: any) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
        isCollapsed,
        openItems,
        onToggle,
        onItemClick,
      }),
    []
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
  isCollapsed,
  openItems,
  onToggle,
  onItemClick,
}: {
  acc: JSX.Element[];
  depth: number;
  item: Item;
  pathname?: string | null;
  isCollapsed: boolean;
  openItems: Set<string>;
  onToggle: (title: string) => void;
  onItemClick: (title: string, hasChildren: boolean, depth: number) => void;
}): Array<JSX.Element> => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path!) : false;
  const exactMatch = checkPath ? pathname === item.path : false;
  const underDevelopment = item.underDevelopment ?? false;
  const hasAccess = permissionCheck(item);

  if (underDevelopment) {
    return acc;
  }

  if (!hasAccess) {
    return acc;
  }

  const hasChildren = !!item.items;
  const isOpen = openItems.has(item.title);

  if (hasChildren) {
    acc.push(
      <SideNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        activeIcon={item.activeIcon}
        key={item.title}
        label={item.label}
        open={isOpen}
        title={item.title}
        path={item.path}
        isCollapsed={isCollapsed}
        onToggle={() => onToggle(item.title)}
        onItemClick={() => onItemClick(item.title, hasChildren, depth)}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            ml: isCollapsed ? 2 : 3,
            borderLeft: "1px solid #818288",
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
            isCollapsed,
            openItems,
            onToggle,
            onItemClick,
          })}
        </Stack>
      </SideNavItem>
    );
  } else {
    acc.push(
      <SideNavItem
        active={exactMatch || partialMatch}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        activeIcon={item.activeIcon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={item.title}
        isCollapsed={isCollapsed}
        onItemClick={() => onItemClick(item.title, hasChildren, depth)}
        onToggle={() => onToggle(item.title)}
      />
    );
  }

  return acc;
};

const SideNavSection = (props: CustomProps) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const handleToggle = useCallback((title: string) => {
    setOpenItems((prevOpenItems) => {
      const newOpenItems = new Set(prevOpenItems);
      if (newOpenItems.has(title)) {
        newOpenItems.delete(title);
      } else {
        newOpenItems.add(title);
      }
      return newOpenItems;
    });
  }, []);

  const handleItemClick = useCallback(
    (title: string, hasChildren: boolean, depth: number) => {
      if (hasChildren && depth === 0) {
        setOpenItems((prevOpenItems) => {
          const newOpenItems: any = new Set<string>();
          for (const item of newOpenItems) {
            if (!prevOpenItems && item !== title && depth === 0) {
              newOpenItems.delete(item);
            }
          }
          if (!prevOpenItems && newOpenItems.has(title)) {
            newOpenItems.delete(title);
          } else {
            newOpenItems.add(title);
          }

          return newOpenItems;
        });
      }
      if (!hasChildren && depth === 0) {
        setOpenItems(new Set());
      }
    },
    [handleToggle]
  );

  return (
    <Stack
      component="ul"
      spacing={1}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        alignItems: props.isCollapsed ? "center" : "normal",
      }}
    >
      {props.subheader && (
        <Box
          component="li"
          sx={{
            color: "var(--nav-section-title-color)",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: "uppercase",
          }}
        >
          {props.subheader}
        </Box>
      )}
      {renderItems({
        items: props.items ?? [],
        pathname: props.pathname,
        isCollapsed: props.isCollapsed,
        openItems,
        onToggle: handleToggle,
        onItemClick: handleItemClick,
      })}
    </Stack>
  );
};

export default SideNavSection;
